import { CommonModule, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { LOCALE_ID, NgModule } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';

import { environment } from '@s-env/environment';

import { AppRoutingModule } from '@s-app/app-routing.module';
import { AppComponent } from '@s-app/app.component';

import { SidenavComponent } from '@s-layout/sidenav/sidenav.component';
import { E404Component } from '@s-layout/views/errors/404/404.component';

import { DialogConfirmComponent } from '@s-dialogs/dialog-confirm.component';
import { PasswordConfirmComponent } from '@s-dialogs/password-confirm.component';

import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { MaterialModule } from './material.module';
import { LucideAngularModule } from 'lucide-angular';
import { provideNativeDateAdapter } from '@spartan-ng/brain/date-time';
import { provideBrnCalendarI18n } from '@spartan-ng/brain/calendar';
import { CalendarHeaderComponent } from '@s-pages/calendar/components/calendar-header/calendar-header.component';
import { DisplayProfileImgComponent } from '@s-components/display-profile-img/display-profile-img.component';
import { LoadingComponent } from '@s-components/display-loading/loading.component';

registerLocaleData(localeDe, 'de-AT', localeDeExtra);

@NgModule({ declarations: [
        AppComponent,
    ],
    bootstrap: [AppComponent], imports: [
        SidenavComponent,
        CommonModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        DialogConfirmComponent,
        PasswordConfirmComponent,
        MaterialModule,
        ServiceWorkerModule.register('/ngsw-worker.js', { enabled: environment.production }),
        E404Component,
        CalendarHeaderComponent,
        DisplayProfileImgComponent,
        LoadingComponent,
        LucideAngularModule], providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'de-AT' },
        { provide: LOCALE_ID, useValue: 'de-AT' },
        { provide: DateAdapter, useClass: NativeDateAdapter, deps: [MAT_DATE_LOCALE] },
        {
          provide: MAT_DATE_FORMATS,
          useValue: {
            parse: { dateInput: null },
            display: {
              dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
              monthYearLabel: { month: 'long', year: 'numeric' } as Intl.DateTimeFormatOptions,
              dateA11yLabel: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
              monthYearA11yLabel: { month: 'long', year: 'numeric' } as Intl.DateTimeFormatOptions,
            },
          },
        },
        provideNativeDateAdapter(),
        provideBrnCalendarI18n({
            formatWeekdayName: (i: number) => ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][i],
            formatHeader: (m: number, y: number) =>
                new Date(y, m).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
            formatYear: (y: number) => `${y}`,
            formatMonth: (m: number) => ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'][m],
            labelPrevious: () => 'Vorheriger Monat',
            labelNext: () => 'Nächster Monat',
            labelWeekday: (i: number) => ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'][i],
            months: () => ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'] as any,
            years: (start?: number, end?: number) => {
                const s = start ?? new Date().getFullYear() - 100;
                const e = end ?? new Date().getFullYear() + 10;
                return Array.from({ length: e - s + 1 }, (_, i) => s + i);
            },
            firstDayOfWeek: () => 1,
        }),
        provideHttpClient(withInterceptorsFromDi()),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideFirestore(() => getFirestore()),
        provideStorage(() => getStorage()),
        provideAuth(() => getAuth()),
        provideFunctions(() => getFunctions(undefined, 'europe-west1')),
        provideMessaging(() => getMessaging()),
        provideAnalytics(() => getAnalytics()),
        providePerformance(() => getPerformance()),
    ] })
export class AppModule {
}
